import React, { useState, useEffect, useCallback } from "react";
import "./App.css";
import TasteProfile from "./components/TasteProfile";
import Discovery from "./components/Discovery";
import Reviews from "./components/Reviews";
import ConnectedAccounts from "./components/ConnectedAccounts";
import Auth from "./components/Auth";
import { supabase, isSupabaseConfigured } from "./supabaseClient";

function App() {
  const [cities, setCities] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [minLikes, setMinLikes] = useState(2);
  const [concerts, setConcerts] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [formData, setFormData] = useState(null);
  const [youtubeAuthToken, setYoutubeAuthToken] = useState(null);
  const [priceModal, setPriceModal] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('shows'); // 'taste', 'social', 'shows', 'reviews', 'people'
  const [user, setUser] = useState(null);
  const [userId, setUserId] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [userLikes, setUserLikes] = useState(null); // Store user's music likes (for future use in recommendations)

  // Initialize dark mode from localStorage or system preference
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setDarkMode(savedTheme === 'dark');
    } else {
      setDarkMode(window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
  }, []);

  // Update theme when darkMode changes
  useEffect(() => {
    document.body.classList.toggle('dark-mode', darkMode);
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  // Get API URL from environment variable, fallback to localhost for development
  const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:5000";
  
  // Log API URL on component mount for debugging (only in development)
  useEffect(() => {
    console.log('🔗 API URL being used:', API_URL);
    console.log('🌍 Environment:', process.env.NODE_ENV);
  }, [API_URL]);

  // Fetch user's music likes after authentication
  const fetchUserLikes = useCallback(async (currentUserId) => {
    const uid = currentUserId || userId;
    if (!uid) return;

    try {
      // Try to get from Supabase first
      if (isSupabaseConfigured()) {
        const { data } = await supabase
          .from('user_artists')
          .select('*, artists(*)')
          .eq('user_id', uid)
          .limit(100);

        if (data && data.length > 0) {
          // Convert to format expected by app
          const likes = {};
          data.forEach(item => {
            const artistName = item.artists?.name || 'Unknown';
            likes[artistName] = item.total_likes || item.affinity_score * 10;
          });
          setUserLikes(likes);
          return;
        }
      }

      // If no data in Supabase, try to fetch from Spotify/YouTube
      // This will be triggered when user connects their music service
    } catch (err) {
      console.error('Error fetching user likes:', err);
    }
  }, [userId]);

  // Save Spotify data to Supabase after authentication
  const saveSpotifyDataToSupabase = async () => {
    if (!userId || !isSupabaseConfigured()) return;

    try {
      const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:5000";
      
      // Get Spotify user data and likes
      const response = await fetch(`${API_URL}/api/spotify/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Include session cookies
        body: JSON.stringify({ user_id: userId })
      });

      if (response.ok) {
        await response.json(); // Response handled
        console.log('Spotify data synced to Supabase');
        // Refresh user likes
        await fetchUserLikes(userId);
      }
    } catch (err) {
      console.error('Error syncing Spotify data:', err);
    }
  };

  // Check for existing Supabase session
  useEffect(() => {
    if (isSupabaseConfigured()) {
      // Check for existing session
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) {
          setUser(session.user);
          setUserId(session.user.id);
          // Auto-fetch user likes
          fetchUserLikes(session.user.id);
        }
      });

      // Listen for auth changes
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          if (session) {
            setUser(session.user);
            setUserId(session.user.id);
            setShowAuth(false);
            // Auto-fetch user likes after auth
            await fetchUserLikes(session.user.id);
          } else {
            setUser(null);
            setUserId(null);
            setUserLikes(null);
          }
        }
      );

      return () => subscription.unsubscribe();
    }

    // Handle OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('code') && window.location.pathname === '/auth/callback') {
      // Supabase will handle this automatically, but we can show loading
      console.log('Processing OAuth callback...');
    }
  }, [fetchUserLikes]);


  const handleSpotifyLogin = async () => {
    try {
      console.log('Attempting Spotify login, API_URL:', API_URL);
      const response = await fetch(`${API_URL}/spotify-login`);
      console.log('Spotify login response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Spotify login response data:', data);
      
      if (data.auth_url) {
        // Open Spotify auth in a popup
        const width = 500;
        const height = 700;
        const left = (window.screen.width / 2) - (width / 2);
        const top = (window.screen.height / 2) - (height / 2);
        
        const popup = window.open(
          data.auth_url,
          'Spotify Login',
          `width=${width},height=${height},left=${left},top=${top}`
        );

        // Listen for the authentication result
        window.addEventListener('message', async (event) => {
          if (event.data === 'spotify-login-success') {
            if (popup) popup.close();
            console.log('Spotify authentication successful');
            
            // Save Spotify data to Supabase if user is logged in
            if (userId && isSupabaseConfigured()) {
              await saveSpotifyDataToSupabase();
            }
            
            // Proceed with the search using spotify as source
            performSearch('spotify');
          } else if (event.data === 'spotify-login-error') {
            if (popup) popup.close();
            setError('Spotify authentication failed');
            setLoading(false);
          }
        }, { once: true });
      }
    } catch (err) {
      console.error("Spotify login error:", err);
      setError(`Failed to initialize Spotify login: ${err.message}. Check console for details.`);
      setLoading(false);
    }
  };

  const handleYouTubeLogin = async () => {
    try {
      console.log('Attempting YouTube login, API_URL:', API_URL);
      // Pass user_id if available so token can be stored in Supabase
      const response = await fetch(`${API_URL}/youtube-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId })
      });
      console.log('YouTube login response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('YouTube login response data:', data);
      
      if (data.auth_url) {
        // Open YouTube auth in a popup
        const width = 500;
        const height = 700;
        const left = (window.screen.width / 2) - (width / 2);
        const top = (window.screen.height / 2) - (height / 2);
        
        const popup = window.open(
          data.auth_url,
          'YouTube Login',
          `width=${width},height=${height},left=${left},top=${top}`
        );

        // Listen for the authentication result
        window.addEventListener('message', async (event) => {
          console.log('Received message:', event.data);
          if (event.data && event.data.type === 'youtube-login-success') {
            if (popup) popup.close();
            console.log('YouTube authentication successful, token:', event.data.token);
            // Store the auth token
            const token = event.data.token;
            setYoutubeAuthToken(token);
            // Pass token directly to avoid React state timing issues
            performSearch('youtube', token);
          } else if (event.data === 'youtube-login-error') {
            if (popup) popup.close();
            setError('YouTube authentication failed');
            setLoading(false);
          }
        }, { once: true });
      } else if (data.error) {
        setError(data.error);
        setLoading(false);
      }
    } catch (err) {
      console.error("YouTube login error:", err);
      setError(`Failed to initialize YouTube login: ${err.message}. Check console for details.`);
      setLoading(false);
    }
  };

  const handleSourceSelect = async (source) => {
    setShowSourceModal(false);
    
    if (source === 'spotify') {
      await handleSpotifyLogin();
    } else if (source === 'youtube') {
      await handleYouTubeLogin();
    }
  };

  const performSearch = async (source, providedToken = null, formDataOverride = null) => {
    try {
      // Use provided form data or state form data
      const searchFormData = formDataOverride || formData;
      if (!searchFormData) {
        setError('Please fill in the search form');
        setLoading(false);
        return;
      }

      // Use provided token or state token
      const authToken = providedToken || youtubeAuthToken;
      console.log('Performing search, API_URL:', API_URL, 'Source:', source, 'Token:', authToken ? 'Present' : 'Missing');
      const requestBody = { 
        ...searchFormData,
        source: source,
        user_id: userId  // Pass user_id so backend can use stored tokens
      };
      
      // Add YouTube auth token if using YouTube (only if explicitly provided)
      // Backend will try stored token if not provided
      if (source === 'youtube' && authToken) {
        requestBody.youtube_auth_token = authToken;
        console.log('Sending YouTube auth token with request');
      } else if (source === 'youtube' && !authToken && !userId) {
        // Only error if we have no token AND no user_id (can't use stored token)
        console.error('YouTube auth token missing and no user_id!');
        setError('YouTube authentication required. Please sign in first.');
        setLoading(false);
        return;
      }
      
      console.log('Request body:', { ...requestBody, youtube_auth_token: source === 'youtube' ? '***' : undefined });
      const response = await fetch(`${API_URL}/concerts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });
      
      console.log('Search response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Search response data:', data);
      
      if (data.success) {
        setConcerts(data.concerts);
        setStats({
          totalArtists: data.total_artists,
          totalConcerts: data.total_concerts
        });
      } else {
        // Check if authentication is needed
        if (data.needs_auth && data.auth_type === 'youtube') {
          // Check account status first - if account shows as connected, don't trigger auth
          if (userId) {
            try {
              const statusResponse = await fetch(`${API_URL}/api/accounts/status?user_id=${userId}`);
              if (statusResponse.ok) {
                const accountStatus = await statusResponse.json();
                if (accountStatus.youtube_connected) {
                  const detail = data.error ? ` ${data.error}` : '';
                  setError(
                    `YouTube could not load fresh likes for this search (your Taste tab may use saved data).${detail} Try again, or reconnect YouTube from Connected Accounts.`
                  );
                  setLoading(false);
                  return;
                }
              }
            } catch (statusErr) {
              console.error('Error checking account status:', statusErr);
            }
            // Account not connected - show error but don't auto-trigger auth
            setError("YouTube account not connected. Please connect your YouTube Music account from the Connected Accounts page.");
          } else {
            setError("YouTube authentication required. Please sign in first.");
            setYoutubeAuthToken(null); // Clear invalid token
            await handleYouTubeLogin();
          }
        } else {
          setError(data.error || "Failed to fetch concerts.");
        }
      }
    } catch (err) {
      console.error("Error details:", err);
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleComparePrices = async (concert) => {
    setPriceLoading(true);
    setPriceModal(null);
    
    try {
      const response = await fetch(`${API_URL}/compare-prices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          event_name: concert.name,
          artist_name: concert.matching_artist || '',
          venue_name: concert.venue,
          city: concert.city || '',
          date: concert.date
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setPriceModal({
          event: concert,
          vendors: data.vendors
        });
      } else {
        setError('Failed to compare prices');
      }
    } catch (err) {
      console.error('Price comparison error:', err);
      setError('Failed to compare prices');
    } finally {
      setPriceLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setConcerts([]);
    setLoading(true);

    const cityList = cities.split(",").map((city) => city.trim());
    
    // Store form data for after source selection
    const formDataObj = {
      cities: cityList,
      start_date: startDate,
      end_date: endDate,
      min_likes: parseInt(minLikes)
    };
    setFormData(formDataObj);

    // Check account status if user is logged in
    if (userId) {
      try {
        const statusResponse = await fetch(`${API_URL}/api/accounts/status?user_id=${userId}`);
        if (statusResponse.ok) {
          const accountStatus = await statusResponse.json();
          const hasSpotify = accountStatus.spotify_connected;
          const hasYouTube = accountStatus.youtube_connected;
          
          // If both are connected, use both (preferred)
          if (hasSpotify && hasYouTube) {
            console.log('[SHOWS] Both accounts connected - using both sources');
            await performSearch('both', null, formDataObj);
            return;
          }
          // If only one is connected, use that one
          else if (hasSpotify) {
            console.log('[SHOWS] Only Spotify connected - using Spotify');
            await performSearch('spotify', null, formDataObj);
            return;
          }
          else if (hasYouTube) {
            console.log('[SHOWS] Only YouTube connected - using YouTube');
            await performSearch('youtube', null, formDataObj);
            return;
          }
        }
      } catch (err) {
        console.error('Error checking account status:', err);
        // Fall through to show source modal
      }
    }

    // If no accounts connected or not logged in, show source selection modal
    setShowSourceModal(true);
  };

  return (
    <div className={`app-container ${darkMode ? 'dark-mode' : ''}`}>
      <nav className="navbar">
        <div className="nav-logo">StageQuest</div>
        <div className="nav-links">
          <button 
            className={activeSection === 'taste' ? 'active' : ''}
            onClick={() => setActiveSection('taste')}
          >
            Taste
          </button>
          <button 
            className={activeSection === 'social' ? 'active' : ''}
            onClick={() => setActiveSection('social')}
          >
            Social
          </button>
          <button 
            className={activeSection === 'shows' ? 'active' : ''}
            onClick={() => setActiveSection('shows')}
          >
            Shows
          </button>
          <button 
            className={activeSection === 'reviews' ? 'active' : ''}
            onClick={() => setActiveSection('reviews')}
          >
            Reviews
          </button>
          <button 
            className={activeSection === 'accounts' ? 'active' : ''}
            onClick={() => setActiveSection('accounts')}
            title="Manage your connected music accounts"
          >
            Connected Accounts
          </button>
          {/* People section will be added later */}
        </div>
        <div className="nav-right">
          {user ? (
            <div className="user-menu">
              <span className="user-email">{user.email}</span>
              <button 
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  
                  try {
                    console.log('Sign out clicked');
                    
                    // Clear state immediately for better UX
                    setUser(null);
                    setUserId(null);
                    setUserLikes(null);
                    
                    if (isSupabaseConfigured() && supabase) {
                      console.log('Calling supabase.auth.signOut()');
                      const { error } = await supabase.auth.signOut();
                      if (error) {
                        console.error('Sign out error:', error);
                        // State already cleared above
                      } else {
                        console.log('Sign out successful');
                        // State already cleared above, onAuthStateChange will also fire
                      }
                    } else {
                      console.log('Supabase not configured, cleared local state only');
                    }
                  } catch (err) {
                    console.error('Error signing out:', err);
                    // State already cleared above
                  }
                }}
                className="btn-secondary"
                type="button"
              >
                Sign Out
              </button>
            </div>
          ) : (
            <button 
              onClick={() => setShowAuth(true)}
              className="btn-primary"
            >
              Sign In
            </button>
          )}
          <div className="theme-toggle">
            <label className="switch">
              <input
                type="checkbox"
                checked={darkMode}
                onChange={() => setDarkMode(!darkMode)}
              />
              <span className="slider round"></span>
            </label>
          </div>
        </div>
      </nav>

      {showSourceModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Choose Your Music Source</h2>
            <p>Select where you listen to your music to find relevant concerts:</p>
            <div className="source-buttons">
              <button 
                className="source-button youtube"
                onClick={() => handleSourceSelect('youtube')}
              >
                <span className="source-icon">🎥</span>
                YouTube
              </button>
              <button 
                className="source-button spotify"
                onClick={() => handleSourceSelect('spotify')}
              >
                <span className="source-icon">🎵</span>
                Spotify
              </button>
            </div>
            <button 
              className="modal-close"
              onClick={() => {
                setShowSourceModal(false);
                setLoading(false);
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      {showAuth && (
        <div className="modal-overlay" onClick={() => setShowAuth(false)}>
          <div className="modal-content auth-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowAuth(false)}>×</button>
            <Auth onAuthSuccess={() => setShowAuth(false)} />
          </div>
        </div>
      )}

      <main className="main-content">
        {!user && !showAuth && (
          <div className="welcome-banner">
            <h1>Welcome to StageQuest</h1>
            <p>Sign in to discover concerts and connect with music lovers</p>
            <button onClick={() => setShowAuth(true)} className="btn-primary">
              Get Started
            </button>
            <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
              Or click <strong>"Sign In"</strong> in the top right corner
            </p>
          </div>
        )}

        {user && (
          <>
            {activeSection === 'taste' && (
              <TasteProfile API_URL={API_URL} userId={userId} userLikes={userLikes} />
            )}
            
            {activeSection === 'social' && (
              <Discovery API_URL={API_URL} userId={userId} userLikes={userLikes} />
            )}
            
            {activeSection === 'reviews' && (
              <Reviews API_URL={API_URL} userId={userId} userLikes={userLikes} />
            )}
            
            {activeSection === 'accounts' && (
              <ConnectedAccounts API_URL={API_URL} userId={userId} />
            )}
          </>
        )}
        
        {activeSection === 'shows' && (
          <>
            <div className="hero-section">
              <h1>Helping you on your quest to finding the best live music near you.</h1>
              
              <form className="search-form" onSubmit={handleSubmit}>
            <div className="search-container">
              <div className="input-row">
                <div className="input-group cities-group">
                  <label htmlFor="cities">Cities:</label>
                  <input
                    id="cities"
                    type="text"
                    value={cities}
                    onChange={(e) => setCities(e.target.value)}
                    placeholder="Enter cities (comma-separated)"
                    className="search-input"
                  />
                </div>
              </div>
              <div className="input-row">
                <div className="input-group date-group">
                  <label htmlFor="start-date">Start Date:</label>
                  <input
                    id="start-date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="date-input"
                  />
                </div>
                <div className="input-group date-group">
                  <label htmlFor="end-date">End Date:</label>
                  <input
                    id="end-date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="date-input"
                  />
                </div>
                <div className="input-group likes-group">
                  <label htmlFor="min-likes">Min. Liked Songs:</label>
                  <input
                    id="min-likes"
                    type="number"
                    min="1"
                    value={minLikes}
                    onChange={(e) => setMinLikes(e.target.value)}
                    className="number-input"
                  />
                </div>
              </div>
              <div className="input-row">
                <button type="submit" className="search-button" disabled={loading}>
                  {loading ? "Searching..." : "Search"}
                </button>
              </div>
            </div>
          </form>
        </div>

        {error && <div className="error-message">{error}</div>}

        {stats && (
          <div className="search-stats">
            <p>Found {stats.totalConcerts} concerts in selected cities</p>
          </div>
        )}

        {concerts.length > 0 && (
          <section className="suggested-section">
            <h2>Upcoming Concerts</h2>
            <div className="concerts-grid">
              {concerts.map((concert, index) => (
                <div key={index} className="concert-card">
                  <div className="concert-info">
                    <h3>{concert.name}</h3>
                    <p className="venue">{concert.venue}</p>
                    <p className="city">{concert.city}</p>
                    <p className="concert-date">{concert.date}</p>
                    <div className="concert-actions">
                      {concert.url && (
                        <a href={concert.url} target="_blank" rel="noopener noreferrer" className="ticket-link">
                          Buy Tickets
                        </a>
                      )}
                      <button 
                        onClick={() => handleComparePrices(concert)}
                        className="compare-button"
                        disabled={priceLoading}
                      >
                        {priceLoading ? 'Loading...' : 'Compare Prices'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Price Comparison Modal */}
        {priceModal && (
          <div className="modal-overlay" onClick={() => setPriceModal(null)}>
            <div className="modal-content price-modal" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setPriceModal(null)}>×</button>
              <h2>Compare Prices: {priceModal.event.name}</h2>
              <p className="modal-subtitle">{priceModal.event.venue} • {priceModal.event.date}</p>
              <div className="vendors-list">
                {priceModal.vendors.map((vendor, index) => (
                  <div key={index} className={`vendor-item ${vendor.available ? 'has-price' : ''}`}>
                    <div className="vendor-info">
                      <h3>{vendor.name}</h3>
                      <span className={`vendor-price ${vendor.available ? 'available' : ''}`}>
                        {vendor.price}
                      </span>
                      {vendor.min_price && vendor.max_price && vendor.min_price !== vendor.max_price && (
                        <span className="price-range">
                          Range: ${vendor.min_price.toFixed(2)} - ${vendor.max_price.toFixed(2)}
                        </span>
                      )}
                    </div>
                    <a 
                      href={vendor.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="vendor-link"
                    >
                      {vendor.available ? 'Buy Now' : 'View on ' + vendor.name}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
          </>
        )}
      </main>
    </div>
  );
}

export default App;
